import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { currentLocaleTag } from '../lib/formatting'
import {
  AlertTriangle,
  Banknote,
  Briefcase,
  Calendar,
  CheckSquare,
  Coins,
  FileText,
  Loader2,
  Receipt,
  Target,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react'
import { api } from '../lib/api'
import { formatCents } from '../lib/money'
import UpcomingEventsWidget from '../components/calendar/UpcomingEventsWidget'
import BarChart from '../components/charts/BarChart'
import DonutChart from '../components/charts/DonutChart'
import MultiBarChart from '../components/charts/MultiBarChart'
import {
  type AmountBreakdownPoint,
  type DashboardOverviewResponse,
  type ForecastResponse,
  type IssuerDto,
} from '@toga/shared'
import { useAuth } from '../contexts/AuthContext'
import { useTranslation } from 'react-i18next'

const TOGA = '#a31b41'
const EMERALD = '#10b981'
const AMBER = '#f59e0b'

export default function Dashboard() {
  const { t } = useTranslation('dashboard')
  const auth = useAuth()
  const userName = auth.status === 'authenticated' ? auth.user.fullName : ''

  const now = new Date()
  const [year, setYear] = useState(now.getUTCFullYear())
  const [quarter, setQuarter] = useState<number | ''>('')
  const [month, setMonth] = useState<number | ''>('')
  const [issuerId, setIssuerId] = useState('')
  const [issuers, setIssuers] = useState<IssuerDto[]>([])

  const [data, setData] = useState<DashboardOverviewResponse | null>(null)
  const [forecast, setForecast] = useState<ForecastResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    api
      .get<{ items: IssuerDto[] }>('/issuers')
      .then((r) => setIssuers(r.items))
      .catch(() => setIssuers([]))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams({ year: String(year) })
      if (month) qs.set('month', String(month))
      else if (quarter) qs.set('quarter', String(quarter))
      if (issuerId) qs.set('issuerId', issuerId)
      const [o, f] = await Promise.all([
        api.get<DashboardOverviewResponse>(`/stats/dashboard-overview?${qs.toString()}`),
        api.get<ForecastResponse>(`/stats/forecast?year=${year}`),
      ])
      setData(o)
      setForecast(f)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('overview.errorLoading'))
    } finally {
      setLoading(false)
    }
  }, [year, quarter, month, issuerId, t])

  useEffect(() => {
    void load()
  }, [load])

  const greeting = useMemo(() => {
    const h = new Date().getHours()
    if (h < 14) return t('greeting.morning')
    if (h < 21) return t('greeting.afternoon')
    return t('greeting.evening')
  }, [t])

  const years = useMemo(() => {
    const y = now.getUTCFullYear()
    // 6 años atrás + el próximo, para poder consultar histórico plurianual.
    return [y - 6, y - 5, y - 4, y - 3, y - 2, y - 1, y, y + 1]
  }, [now])

  const monthNames = useMemo(() => {
    const loc = currentLocaleTag()
    return Array.from({ length: 12 }, (_, i) =>
      new Date(Date.UTC(2000, i, 1)).toLocaleDateString(loc, { month: 'long' })
    )
  }, [])

  const periodLabel = month
    ? `${monthNames[Number(month) - 1]} ${year}`
    : quarter
      ? `T${quarter} ${year}`
      : String(year)

  const money = (n: number) => formatCents(n)
  // Eje Y compacto: euros sin decimales (evita que "27.500,00 €" se corte).
  const moneyAxis = (n: number) => `${Math.round(n / 100).toLocaleString(currentLocaleTag())} €`
  const shortMonth = (yyyymm: string) => {
    const m = Number(yyyymm.split('-')[1]) - 1
    if (!Number.isInteger(m) || m < 0 || m > 11) return ''
    return new Date(2000, m, 1).toLocaleDateString(currentLocaleTag(), { month: 'short' })
  }
  const donut = (pts: AmountBreakdownPoint[]) => pts.map((p) => ({ label: p.label, value: p.count }))
  const donutMoney = (pts: AmountBreakdownPoint[]) => pts.map((p) => ({ label: p.label, value: p.amountCents }))

  return (
    <div className="space-y-6 animate-slideUp">
      <header className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-serif font-semibold t-primary">
            {greeting}, <span className="text-toga-700 dark:text-toga-300">{userName.split(' ')[0]}</span>
          </h1>
          <p className="t-muted text-sm">
            {t('subtitle')} · <span className="capitalize">{periodLabel}</span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <PeriodSelect value={year} onChange={(v) => setYear(Number(v))}>
            {years.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </PeriodSelect>
          <PeriodSelect
            value={quarter}
            onChange={(v) => { setQuarter(v === '' ? '' : Number(v)); if (v !== '') setMonth('') }}
          >
            <option value="">{t('overview.period.allQuarters')}</option>
            {[1, 2, 3, 4].map((q) => (
              <option key={q} value={q}>{`T${q}`}</option>
            ))}
          </PeriodSelect>
          <PeriodSelect
            value={month}
            onChange={(v) => { setMonth(v === '' ? '' : Number(v)); if (v !== '') setQuarter('') }}
          >
            <option value="">{t('overview.period.allMonths')}</option>
            {monthNames.map((m, i) => (
              <option key={i} value={i + 1} className="capitalize">{m}</option>
            ))}
          </PeriodSelect>
          {issuers.length > 1 && (
            <PeriodSelect value={issuerId} onChange={(v) => setIssuerId(String(v))}>
              <option value="">{t('issuerGlobal')}</option>
              {issuers.map((i) => (
                <option key={i.id} value={i.id}>{i.name}</option>
              ))}
            </PeriodSelect>
          )}
        </div>
      </header>

      {error && (
        <div className="surface p-3 text-sm rounded-lg border text-rose-700 dark:text-rose-300 bg-rose-500/10 border-rose-500/30 flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {loading && !data ? (
        <div className="t-muted flex items-center gap-2">
          <Loader2 size={14} className="animate-spin" /> {t('loading')}
        </div>
      ) : (
        data && (
          <div className={`space-y-8 ${loading ? 'opacity-60 transition-opacity' : ''}`}>
            {/* ================= OPORTUNIDADES ================= */}
            <section className="space-y-4">
              <SectionTitle icon={Target} title={t('overview.areas.opportunities')} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label={t('overview.opp.new')} value={String(data.opportunities.newCount)} icon={Target} accent="blue" />
                <StatBox label={t('overview.opp.sent')} value={String(data.opportunities.sentCount)} icon={FileText} accent="toga" />
                <StatBox label={t('overview.opp.lost')} value={String(data.opportunities.lostCount)} icon={TrendingDown} accent="rose" />
                <StatBox label={t('overview.opp.won')} value={String(data.opportunities.wonCount)} icon={TrendingUp} accent="emerald" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <ChartCard title={t('overview.opp.byStage')}>
                  <Donut pts={donut(data.opportunities.byStage)} empty={t('overview.noData')} />
                </ChartCard>
                <ChartCard title={t('overview.opp.byServiceLine')}>
                  <Donut pts={donut(data.opportunities.byServiceLine)} empty={t('overview.noData')} />
                </ChartCard>
                <ChartCard title={t('overview.opp.byAdvisor')}>
                  <Donut pts={donut(data.opportunities.byAdvisor)} empty={t('overview.noData')} />
                </ChartCard>
              </div>
            </section>

            {/* ================= PROYECTOS ================= */}
            <section className="space-y-4">
              <SectionTitle icon={Briefcase} title={t('overview.areas.projects')} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label={t('overview.proj.onboarding')} value={String(data.projects.onboardingCount)} icon={Briefcase} accent="blue" />
                <StatBox label={t('overview.proj.execution')} value={String(data.projects.executionCount)} icon={TrendingUp} accent="toga" />
                <StatBox label={t('overview.proj.support')} value={String(data.projects.supportCount)} icon={CheckSquare} accent="amber" />
                <StatBox label={t('overview.proj.finished')} value={String(data.projects.finishedCount)} icon={CheckSquare} accent="emerald" />
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <ChartCard title={t('overview.proj.byPhase')}>
                  <Donut pts={donut(data.projects.byPhase)} empty={t('overview.noData')} />
                </ChartCard>
                <ChartCard title={t('overview.proj.byRoadmap')}>
                  <Donut pts={donut(data.projects.byRoadmap)} empty={t('overview.noData')} />
                </ChartCard>
                <ChartCard title={t('overview.proj.byMilestones')}>
                  <Donut pts={donut(data.projects.byMilestone)} empty={t('overview.noData')} />
                </ChartCard>
              </div>
              <ChartCard title={t('overview.proj.finance')}>
                {data.projects.finance.length > 0 ? (
                  <MultiBarChart
                    data={data.projects.finance.map((p) => ({
                      label: p.label,
                      values: [p.soldCents, p.billedCents, p.collectedCents],
                    }))}
                    series={[
                      { name: t('overview.proj.sold'), color: TOGA },
                      { name: t('overview.proj.billed'), color: '#1B4FD8' },
                      { name: t('overview.proj.collected'), color: EMERALD },
                    ]}
                    formatValue={money}
                    formatAxis={moneyAxis}
                    height={260}
                    rotateLabels
                  />
                ) : (
                  <Empty text={t('overview.noData')} />
                )}
              </ChartCard>
            </section>

            {/* ================= DATOS ECONÓMICOS ================= */}
            <section className="space-y-4">
              <SectionTitle icon={Coins} title={t('overview.areas.economics')} />
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatBox label={t('overview.eco.billed')} value={money(data.economics.billedCents)} icon={Receipt} accent="toga" />
                <StatBox
                  label={t('overview.eco.spent')}
                  value={money(data.economics.spentCents)}
                  icon={TrendingDown}
                  accent="rose"
                  hint={data.economics.investedCents > 0 ? t('overview.eco.investedHint', { value: money(data.economics.investedCents) }) : undefined}
                />
                <StatBox label={t('overview.eco.pendingIssue')} value={money(data.economics.pendingIssueCents)} icon={FileText} accent="amber" />
                <StatBox label={t('overview.eco.pendingCollect')} value={money(data.economics.pendingCollectCents)} icon={Wallet} accent="blue" />
              </div>

              <ChartCard title={t('overview.eco.forecast', { year })}>
                {forecast ? (
                  <BarChart
                    data={forecast.months.map((m) => ({ label: shortMonth(m.month), value: m.totalExpectedCents }))}
                    formatValue={money}
                    formatAxis={moneyAxis}
                    primaryColor={TOGA}
                    height={220}
                  />
                ) : (
                  <Empty text={t('overview.noData')} />
                )}
              </ChartCard>

              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <ChartCard title={t('overview.eco.billingByServiceLine')}>
                  <Donut pts={donutMoney(data.economics.billingByServiceLine)} money empty={t('overview.noData')} />
                </ChartCard>
                <ChartCard title={t('overview.eco.billingByClient')}>
                  <Donut pts={donutMoney(data.economics.billingByClient)} money empty={t('overview.noData')} />
                </ChartCard>
                <ChartCard title={t('overview.eco.expenseByCategory')}>
                  <Donut pts={donutMoney(data.economics.expenseByCategory)} money empty={t('overview.noData')} />
                </ChartCard>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ChartCard title={t('overview.eco.salesByMonth')}>
                  <MultiBarChart
                    data={data.economics.salesByMonth.map((m) => ({
                      label: shortMonth(m.month),
                      values: [m.primaryCents, m.secondaryCents],
                    }))}
                    series={[
                      { name: t('overview.eco.punctual'), color: TOGA },
                      { name: t('overview.eco.recurring'), color: EMERALD },
                    ]}
                    formatValue={money}
                    formatAxis={moneyAxis}
                    height={220}
                  />
                </ChartCard>
                <ChartCard title={t('overview.eco.advisorSales')}>
                  {data.economics.advisorSales.length > 0 ? (
                    <MultiBarChart
                      data={data.economics.advisorSales.map((a) => ({
                        label: a.label,
                        values: [a.soldCents, a.commissionCents],
                      }))}
                      series={[
                        { name: t('overview.eco.sold'), color: TOGA },
                        { name: t('overview.eco.commission'), color: AMBER },
                      ]}
                      formatValue={money}
                      formatAxis={moneyAxis}
                      height={240}
                      rotateLabels
                    />
                  ) : (
                    <Empty text={t('overview.noData')} />
                  )}
                </ChartCard>
              </div>

              <ChartCard title={t('overview.eco.topClients')}>
                {data.economics.topClients.length > 0 ? (
                  <MultiBarChart
                    data={data.economics.topClients.map((c) => ({
                      label: c.clientDisplayName,
                      values: [c.totalCents, c.paidCents],
                    }))}
                    series={[
                      { name: t('overview.eco.billedShort'), color: '#1B4FD8' },
                      { name: t('overview.eco.collectedShort'), color: EMERALD },
                    ]}
                    formatValue={money}
                    formatAxis={moneyAxis}
                    height={240}
                    rotateLabels
                  />
                ) : (
                  <Empty text={t('overview.noData')} />
                )}
              </ChartCard>
            </section>

            {/* ================= ACTIVIDADES Y EVENTOS ================= */}
            <section className="space-y-4">
              <SectionTitle icon={Calendar} title={t('overview.areas.activities')} />
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="surface p-5">
                  <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                    <CheckSquare size={14} className="text-toga-700 dark:text-toga-300" />
                    {t('overview.act.tasks')}
                  </h3>
                  <Link to="/tasks" className="block hover:bg-hover rounded-lg -m-1 p-1 transition">
                    <div className="text-3xl font-semibold t-primary font-mono">
                      {data.activities.myOpenTasksCount}
                      <span className="text-lg t-faint"> / {data.activities.openTasksCount}</span>
                    </div>
                    <div className="text-xs t-muted mt-1">{t('overview.act.tasksHint')}</div>
                  </Link>
                  {data.activities.openTasks.length > 0 ? (
                    <ul className="mt-3 space-y-1 border-t border-base/50 pt-3">
                      {data.activities.openTasks.map((tk) => (
                        <li key={tk.id}>
                          <Link
                            to="/tasks"
                            className="flex items-center justify-between gap-2 text-xs rounded px-1.5 py-1 hover:bg-hover"
                          >
                            <span className="t-secondary truncate">{tk.title}</span>
                            {tk.dueAt && (
                              <span className="t-faint font-mono shrink-0">
                                {new Date(tk.dueAt).toLocaleDateString(currentLocaleTag(), {
                                  day: '2-digit',
                                  month: '2-digit',
                                })}
                              </span>
                            )}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="mt-3 text-xs t-faint border-t border-base/50 pt-3">
                      {t('overview.act.noTasks')}
                    </p>
                  )}
                </div>
                <div className="surface p-5">
                  <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                    <Calendar size={14} className="text-toga-700 dark:text-toga-300" />
                    {t('overview.act.events', { count: data.activities.upcomingEventsCount })}
                  </h3>
                  <UpcomingEventsWidget />
                </div>
                <div className="surface p-5">
                  <h3 className="t-primary font-semibold flex items-center gap-2 mb-3">
                    <Banknote size={14} className="text-toga-700 dark:text-toga-300" />
                    {t('overview.act.shortcuts')}
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <QuickLink to="/billing/new" label={t('shortcuts.newInvoice')} icon={Receipt} />
                    <QuickLink to="/opportunities" label={t('shortcuts.opportunities')} icon={Target} />
                    <QuickLink to="/tasks" label={t('shortcuts.myTasks')} icon={CheckSquare} />
                    <QuickLink to="/agenda" label={t('shortcuts.calendar')} icon={Calendar} />
                    <QuickLink to="/ai" label={t('shortcuts.aiAssistant')} icon={TrendingUp} />
                  </div>
                </div>
              </div>
            </section>
          </div>
        )
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

function PeriodSelect({
  value,
  onChange,
  children,
}: {
  value: string | number
  onChange: (v: string) => void
  children: React.ReactNode
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-transparent border border-base rounded px-2 py-1 text-sm t-secondary focus:outline-none focus:border-toga-500/50"
    >
      {children}
    </select>
  )
}

function SectionTitle({ icon: Icon, title }: { icon: typeof Target; title: string }) {
  return (
    <div className="flex items-center gap-2 border-b border-base/60 pb-2">
      <Icon size={16} className="text-toga-700 dark:text-toga-300" />
      <h2 className="text-sm font-semibold uppercase tracking-wider t-secondary">{title}</h2>
    </div>
  )
}

const ACCENTS: Record<string, string> = {
  toga: 'bg-toga-500/15 text-toga-700 dark:text-toga-300',
  blue: 'bg-blue-500/15 text-blue-700 dark:text-blue-300',
  emerald: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  amber: 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  rose: 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
}

function StatBox({
  label,
  value,
  icon: Icon,
  accent,
  hint,
}: {
  label: string
  value: string
  icon: typeof Target
  accent: keyof typeof ACCENTS | string
  hint?: string
}) {
  return (
    <div className="surface p-4 flex items-start gap-3">
      <div className={`w-10 h-10 rounded-lg grid place-items-center shrink-0 ${ACCENTS[accent] ?? ACCENTS.toga}`}>
        <Icon size={18} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[11px] uppercase tracking-wider t-muted">{label}</div>
        <div className="text-xl font-semibold t-primary mt-0.5 font-mono truncate text-right">{value}</div>
        {hint && <div className="text-[10px] t-faint mt-0.5 text-right">{hint}</div>}
      </div>
    </div>
  )
}

function ChartCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="surface p-5">
      <h3 className="t-primary font-semibold text-sm mb-3">{title}</h3>
      {children}
    </div>
  )
}

function Donut({
  pts,
  money,
  empty,
}: {
  pts: { label: string; value: number }[]
  money?: boolean
  empty: string
}) {
  // Un tramo de cero —un cliente cuya única factura se anuló entera— no se puede
  // dibujar y saldría en la leyenda con 0 %. Se deja fuera aquí y no en el
  // backend porque el dato sí es cierto: facturó y se rectificó.
  const slices = pts.filter((p) => p.value > 0)
  if (slices.length === 0) return <Empty text={empty} />
  return <DonutChart data={slices} formatValue={money ? (n) => formatCents(n) : undefined} />
}

function Empty({ text }: { text: string }) {
  return <div className="text-xs t-faint text-center py-8">{text}</div>
}

function QuickLink({
  to,
  label,
  icon: Icon,
}: {
  to: string
  label: string
  icon: typeof Receipt
}) {
  return (
    <Link
      to={to}
      className="px-3 py-2 rounded-lg border border-base hover:bg-hover hover:border-toga-500/40 text-sm t-secondary flex items-center gap-2"
    >
      <Icon size={14} className="text-toga-700 dark:text-toga-300" />
      {label}
    </Link>
  )
}
